import * as React from 'react';

import { StatsigProvider } from '@coursera-web/statsig';

import { useLocation } from 'js/lib/useRouter';
import { get as userGet } from 'js/lib/user';

import { useEnterpriseMyPageHeader } from 'bundles/composable-page-header-enterprise/contexts/EnterpriseMyPageHeaderContext';
import LoggedInEnterpriseDesktopMainNav from 'bundles/composable-page-header-enterprise/main-nav-modules/desktop/LoggedInEnterpriseDesktopMainNav';
import LoggedInOrgScopedEnterpriseDesktopMainNav from 'bundles/composable-page-header-enterprise/main-nav-modules/desktop/LoggedInOrgScopedEnterpriseDesktopMainNav';
import LoggedOutEnterpriseDesktopMainNav from 'bundles/composable-page-header-enterprise/main-nav-modules/desktop/LoggedOutEnterpriseDesktopMainNav';
import LoggedOutOrgScopedEnterpriseDesktopMainNav from 'bundles/composable-page-header-enterprise/main-nav-modules/desktop/LoggedOutOrgScopedEnterpriseDesktopMainNav';
import LoggedInEnterpriseMobileMainNav from 'bundles/composable-page-header-enterprise/main-nav-modules/mobile/LoggedInEnterpriseMobileMainNav';
import LoggedInOrgScopedEnterpriseMobileMainNav from 'bundles/composable-page-header-enterprise/main-nav-modules/mobile/LoggedInOrgScopedEnterpriseMobileMainNav';
import LoggedOutEnterpriseMobileMainNav from 'bundles/composable-page-header-enterprise/main-nav-modules/mobile/LoggedOutEnterpriseMobileMainNav';
import LoggedOutOrgScopedEnterpriseMobileMainNav from 'bundles/composable-page-header-enterprise/main-nav-modules/mobile/LoggedOutOrgScopedEnterpriseMobileMainNav';
import DefaultEnterprisePageHeaderProvider from 'bundles/composable-page-header-enterprise/providers/DefaultEnterprisePageHeaderProvider';
import { mergeEnterpriseDataWithBackup } from 'bundles/composable-page-header-enterprise/utils/enterpriseProgramDataMerge';
import GlobalPageHeaderContainer from 'bundles/composable-page-header/components/GlobalPageHeaderContainer';
import GlobalPageHeaderStateProvider from 'bundles/composable-page-header/providers/GlobalPageHeaderStateProvider';
import type {
  EnterpriseProgramFromSlugFragment,
  NaptimeThirdPartyOrganizationFromSlugFragment,
} from 'bundles/enterprise-legacy-learner-home/queries/__generated__/EnterpriseProgramBySlugQuery';
import { useIsOrgScopedHome } from 'bundles/unified-common/hooks/useIsOrgScopedHome';

type Props = {
  backupProgram?: EnterpriseProgramFromSlugFragment;
  backupThirdPartyOrg?: NaptimeThirdPartyOrganizationFromSlugFragment;
  hideSearch?: boolean;
};

const EnterprisePageHeader = ({ backupProgram, backupThirdPartyOrg, hideSearch }: Props) => {
  const user = userGet();
  const { pathname } = useLocation();
  const enterpriseMyPageHeader = useEnterpriseMyPageHeader();
  const isOrgScopedHomeEnabled = useIsOrgScopedHome();
  // Use org-scoped nav when the route is explicitly org-scoped, even for logged-out users
  // where the Statsig gate cannot evaluate (no authenticated user).
  const useOrgScopedNav = isOrgScopedHomeEnabled || pathname.startsWith('/organizations/');

  const pageHeaderDataWithBackup = mergeEnterpriseDataWithBackup(
    enterpriseMyPageHeader,
    backupProgram,
    backupThirdPartyOrg
  );

  const getOrgScopedDesktopNav = () => {
    if (user?.id) {
      return (
        <LoggedInOrgScopedEnterpriseDesktopMainNav pageHeaderData={pageHeaderDataWithBackup} hideSearch={hideSearch} />
      );
    } else {
      return (
        <LoggedOutOrgScopedEnterpriseDesktopMainNav pageHeaderData={pageHeaderDataWithBackup} hideSearch={hideSearch} />
      );
    }
  };
  const getOrgScopedMobileNav = () => {
    if (user?.id) {
      return (
        <LoggedInOrgScopedEnterpriseMobileMainNav pageHeaderData={pageHeaderDataWithBackup} hideSearch={hideSearch} />
      );
    } else {
      return (
        <LoggedOutOrgScopedEnterpriseMobileMainNav pageHeaderData={pageHeaderDataWithBackup} hideSearch={hideSearch} />
      );
    }
  };

  const getDesktopNav = () => {
    if (user?.id) {
      return <LoggedInEnterpriseDesktopMainNav pageHeaderData={pageHeaderDataWithBackup} hideSearch={hideSearch} />;
    } else {
      return <LoggedOutEnterpriseDesktopMainNav pageHeaderData={pageHeaderDataWithBackup} hideSearch={hideSearch} />;
    }
  };
  const getMobileNav = () => {
    if (user?.id) {
      return <LoggedInEnterpriseMobileMainNav pageHeaderData={pageHeaderDataWithBackup} hideSearch={hideSearch} />;
    } else {
      return <LoggedOutEnterpriseMobileMainNav pageHeaderData={pageHeaderDataWithBackup} hideSearch={hideSearch} />;
    }
  };

  const DesktopNavComponent = useOrgScopedNav ? getOrgScopedDesktopNav() : getDesktopNav();
  const MobileNavComponent = useOrgScopedNav ? getOrgScopedMobileNav() : getMobileNav();

  return <GlobalPageHeaderContainer desktopMainNav={DesktopNavComponent} mobileMainNav={MobileNavComponent} />;
};

const EnterprisePageHeaderWithProvider = ({ backupProgram, backupThirdPartyOrg, hideSearch }: Props) => {
  return (
    <StatsigProvider>
      <GlobalPageHeaderStateProvider additionalDefaultPageHeaderHeight={undefined}>
        <DefaultEnterprisePageHeaderProvider>
          <EnterprisePageHeader
            backupProgram={backupProgram}
            backupThirdPartyOrg={backupThirdPartyOrg}
            hideSearch={hideSearch}
          />
        </DefaultEnterprisePageHeaderProvider>
      </GlobalPageHeaderStateProvider>
    </StatsigProvider>
  );
};

export default EnterprisePageHeaderWithProvider;
